import React, { ReactNode } from "react";

interface AdminTabBoundaryProps {
  /** Tab key — used as a label in the fallback and to reset on tab change. */
  tab: string;
  children: ReactNode;
}

interface AdminTabBoundaryState {
  error: Error | null;
}

/**
 * AdminTabBoundary — isolates one admin-console tab.
 *
 * A render/query failure inside a single tab (e.g. a Convex backend deploy
 * skew where a function is missing, or a transient network error) would
 * otherwise bubble to the app-level AppErrorBoundary and crash the ENTIRE
 * workbench with a full-screen "Reload" card. Wrapping each self-querying
 * tab here degrades that to an inline message — the rest of the admin
 * console and workbench keep working, matching how TeamRolesTab already
 * fails gracefully. Each tab mounts its own boundary, so navigating to a
 * different tab and back clears the error.
 */
export class AdminTabBoundary extends React.Component<
  AdminTabBoundaryProps,
  AdminTabBoundaryState
> {
  state: AdminTabBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AdminTabBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface in the console for diagnosis without taking down the app.
    console.error(`AdminTabBoundary[${this.props.tab}] caught:`, error, info.componentStack);
  }

  componentDidUpdate(prevProps: AdminTabBoundaryProps): void {
    // Today each tab conditionally mounts its own boundary, so a tab switch
    // already unmounts+resets this. Clearing on a `tab` prop change keeps the
    // component correct if it's ever reused while staying mounted across tabs.
    if (prevProps.tab !== this.props.tab && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="ac-error-banner" role="alert">
          This panel failed to load. Try another tab or reload the app.
        </div>
      );
    }
    return this.props.children;
  }
}
